import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";

import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import TeamPill from "components/team-pill";
import { TEAMS_FEATURE } from "constants/feature-flags";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  ALREADY_TAKEN_MESSAGE,
  NOT_A_MEMBER_MESSAGE
} from "features/staff-tasks-v2/components/staff-task-modal-dialog/view-staff-task-form/team-task-banner.component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import {
  formatWaiting,
  isWaitingHot
} from "features/staff-tasks-v2/utils/team-task.utils";
import {
  STAFF_TASKS_WITH_TEAM_QUERY,
  useGetStaffTasksQuery,
  useTakeStaffTaskMutation
} from "services/staff-tasks";
import type { StaffTask } from "services/types/staff-tasks.type";

/** The most rows the section asks for; more than this shows "50+" in its header. */
export const UNCLAIMED_DRAWER_LIMIT = 50;

const errorStatus = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null && "status" in error
    ? Number((error as { status: unknown }).status)
    : undefined;

function UnclaimedRow({
  task,
  onOpen
}: {
  task: StaffTask;
  onOpen: (taskId: number) => void;
}) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [takeTask] = useTakeStaffTaskMutation();
  const [isTaking, setIsTaking] = useState(false);

  const handleTake = useCallback(async () => {
    setIsTaking(true);
    try {
      await takeTask(task.id).unwrap();
      onOpen(task.id);
    } catch (error) {
      const status = errorStatus(error);
      dispatch(
        setCustomMessage(
          status === 400
            ? ALREADY_TAKEN_MESSAGE
            : status === 403
              ? NOT_A_MEMBER_MESSAGE
              : "Could not take the task. Try again."
        )
      );
      dispatch(setSnackbarOpen(true));
    } finally {
      setIsTaking(false);
    }
  }, [dispatch, onOpen, takeTask, task.id]);

  const hot = isWaitingHot(task.created_at);
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      data-testid={`unclaimed-team-task-${task.id}`}
      sx={{
        px: 1.5,
        py: 1.25,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.paper"
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          color="text.primary"
          onClick={() => onOpen(task.id)}
          sx={{
            fontWeight: 700,
            fontSize: 16,
            textAlign: "left",
            overflowWrap: "anywhere"
          }}
        >
          {task.title}
        </Link>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ mt: 0.5 }}
        >
          {task.team && (
            <TeamPill name={task.team.name} color={task.team.color} />
          )}
          {task.patient?.fullName && (
            <Typography variant="body2" color="text.secondary">
              {task.patient.fullName}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: hot ? "error.main" : "text.secondary",
              fontWeight: hot ? 700 : 400
            }}
          >
            Waiting {formatWaiting(task.created_at)}
          </Typography>
        </Stack>
      </Box>
      <LoadingButton
        variant="contained"
        loading={isTaking}
        onClick={handleTake}
        data-testid={`unclaimed-team-task-take-${task.id}`}
      >
        Take it
      </LoadingButton>
    </Stack>
  );
}

/**
 * Teams (TEAMS_FEATURE): "Unclaimed in my teams" at the top of the drawer's "Assigned To Me". An
 * unclaimed team task has no person, so it is in nobody's "Assigned To Me"; before this, members could
 * only find it on Home → For me. Oldest first, with Take it. Hidden when empty, and for anyone on no
 * team (the request then answers nothing).
 *
 * `exclude_assignee` leaves out a task I already collaborate on, which "Assigned To Me" already lists.
 * The tab's own count is deliberately unchanged: it still counts only tasks that are mine.
 */
export default function UnclaimedTeamTasks() {
  const theme = useTheme();
  const teamsEnabled = Boolean(useFlags()[TEAMS_FEATURE]);
  const currentUserId = Number(useAppSelector(selectCurrentUser)?.id) || 0;
  const { isPatientViewMode, patientId, handleOpenTaskModal } =
    useStaffTasksV2();
  const skip = !teamsEnabled || currentUserId <= 0;

  const { data } = useGetStaffTasksQuery(
    {
      my_teams: true,
      unclaimed: true,
      exclude_assignee: currentUserId,
      is_complete: false,
      is_active: true,
      patient: isPatientViewMode ? patientId : undefined,
      query: STAFF_TASKS_WITH_TEAM_QUERY,
      ordering: "created_at",
      limit: UNCLAIMED_DRAWER_LIMIT
    },
    { skip }
  );

  const openTask = useCallback(
    (taskId: number) => handleOpenTaskModal({ mode: "view", taskId }),
    [handleOpenTaskModal]
  );

  // RTK keeps the last answer under `skip`, so the flag going off must hide it explicitly.
  const tasks = skip ? [] : (data ?? []);
  if (!tasks.length) return null;

  const tone = theme.palette.warning.main;
  return (
    <Box
      data-testid="unclaimed-team-tasks"
      sx={{
        mb: 2,
        border: `1px solid ${alpha(tone, 0.5)}`,
        borderRadius: 2,
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 1.5, py: 1, backgroundColor: alpha(tone, 0.14) }}
      >
        <GroupsOutlinedIcon fontSize="small" />
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
          Unclaimed in my teams
        </Typography>
        <Typography variant="body2" color="text.secondary">
          · {tasks.length}
          {tasks.length === UNCLAIMED_DRAWER_LIMIT ? "+" : ""} · first to click
          Take it owns it
        </Typography>
      </Stack>
      {tasks.map((task) => (
        <UnclaimedRow key={task.id} task={task} onOpen={openTask} />
      ))}
    </Box>
  );
}
